// src/oura/merchant/HomeInsights.tsx
//
// How often each AI engine names the merchant, as Visibility's rings, inside
// Home's "Each AI assistant" card (HomeNumbers.tsx).
//
// It reads Home's own reading (MerchantLive), which is built from the same
// model as Visibility, so Home and Visibility cannot disagree. Nothing here
// is fetched or worked out on its own. An engine the scan did not measure
// says so; it is never drawn as an empty ring, because an empty ring reads as
// "named nowhere".

import { EngineLogo, Ring, TYPE } from "@oura/ui";
import { cn } from "../lib/cn";
import type { MerchantLive } from "./model";

/**
 * The three engines as Visibility's tiles draw them (VisTiles): a 64px ring
 * with the share in it, the engine's logo and name under it, then "37 of 50",
 * in one row.
 * Not measured is the dashed track and a dash, never a ring at zero.
 */
export function EngineRingRow({ engines }: { engines: NonNullable<MerchantLive["engines"]> }) {
    return (
        // One row of three, as on Visibility.
        <div className="grid grid-cols-3 gap-2">
            {engines.map((e) => {
                const value = e.named !== null && e.of !== null && e.of > 0 ? Math.round((e.named / e.of) * 100) : null;
                return (
                    <div key={e.key} className="flex min-w-0 flex-col items-center py-1 text-center">
                        <Ring value={value} dashed={value === null}>
                            <span className={cn(TYPE.tc, "font-semibold tabular-nums", value === null ? "text-gray-400" : "text-gray-900")}>
                                {value === null ? "–" : `${value}%`}
                            </span>
                        </Ring>
                        <span className={cn(TYPE.tc, "mt-2 flex max-w-full flex-wrap items-center justify-center gap-x-1.5 gap-y-0.5 font-medium text-gray-900")}>
                            <EngineLogo engine={e.key} size={14} />
                            <span>{e.label}</span>
                        </span>
                        <span className={cn(TYPE.tm, "tabular-nums text-gray-500")}>{e.named !== null && e.of !== null ? `${e.named} of ${e.of}` : "Not read"}</span>
                    </div>
                );
            })}
        </div>
    );
}
